import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '../../Button';
import { toast } from 'react-toastify';
import _ from 'lodash';
import Ai from '../../../store/models/Ai';

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

const DEFAULT_SCHEDULE = {
  enabled: true,
  timezone: 'UTC',
  schedule: {
    monday: { enabled: true, start: '09:00', end: '17:00' },
    tuesday: { enabled: true, start: '09:00', end: '17:00' },
    wednesday: { enabled: true, start: '09:00', end: '17:00' },
    thursday: { enabled: true, start: '09:00', end: '17:00' },
    friday: { enabled: true, start: '09:00', end: '17:00' },
    saturday: { enabled: true, start: '09:00', end: '17:00' },
    sunday: { enabled: true, start: '09:00', end: '17:00' }
  }
};

const transformApiScheduleToComponent = (apiSchedule) => {
  if (!apiSchedule) {
    return DEFAULT_SCHEDULE;
  }

  const schedule = {};
  
  // Transform the API format where schedule is an object with day keys
  if (apiSchedule.schedule && typeof apiSchedule.schedule === 'object') {
    Object.keys(apiSchedule.schedule).forEach(dayKey => {
      const dayData = apiSchedule.schedule[dayKey];
      if (dayData && dayData.enabled) {
        // Convert time format from "HH:mm:ss" to "HH:mm" for input fields
        const formatTime = (time) => time ? time.substring(0, 5) : '09:00';
        
        schedule[dayKey] = {
          enabled: dayData.enabled,
          start: formatTime(dayData.start),
          end: formatTime(dayData.end)
        };
      }
    });
  }

  return {
    enabled: apiSchedule.enabled,
    timezone: apiSchedule.timezone || 'UTC',
    schedule
  };
};

const generateTimezoneOptions = () => {
  const timezones = [
    { value: 'UTC', label: 'UTC' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
    { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
    { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
    { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' }
  ];
  return timezones;
};

export const SupportSchedule = ({ chatbot, projectId }) => {
  const dispatch = useDispatch();

  // Initialize with default schedule
  const currentSchedule = DEFAULT_SCHEDULE;
  
  const [schedule, setSchedule] = useState(currentSchedule);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supportStatus, setSupportStatus] = useState('online');

  // Fetch support status from API
  const fetchSupportStatus = async () => {
    if (!chatbot?.id || !projectId) return;
    
    try {
      const statusResponse = await dispatch(Ai.actions.fetchSupportStatus(chatbot.id, projectId));
      if (statusResponse && statusResponse.status) {
        setSupportStatus(statusResponse.status);
      }
    } catch (error) {
      console.error('Error fetching support status:', error);
      // Default to online if API fails
      setSupportStatus('online');
    }
  };

  // Fetch schedule data from new API
  const fetchScheduleData = async () => {
    if (!chatbot?.id || !projectId) return;
    
    setLoading(true);
    try {
      // Fetch both schedule and status in parallel
      const [apiSchedule] = await Promise.all([
        dispatch(Ai.actions.fetchSupportSchedule(chatbot.id, projectId)),
        fetchSupportStatus()
      ]);

      if (apiSchedule) {
        const transformedSchedule = transformApiScheduleToComponent(apiSchedule);
        setSchedule(transformedSchedule);
      } else {
        setSchedule({ enabled: false, timezone: 'UTC', schedule: {} });
      }
    } catch (error) {
      if (error.message?.includes('404') || error.code === 404) {
        // 404 means no schedule exists yet - this is not an error
        setSchedule({ enabled: false, timezone: 'UTC', schedule: {} });
      } else {
        console.error('Error fetching support schedule:', error);
        setSchedule({ enabled: false, timezone: 'UTC', schedule: {} });
      }
    } finally {
      setLoading(false);
    }
  };

  // Update form state when chatbot prop changes
  useEffect(() => {
    fetchScheduleData();
  }, [chatbot?.id, projectId]);

  // Check if TalkJS is configured
  const isTalkJSConfigured = React.useMemo(() => {
    const appId = _.get(chatbot, 'ui.talkjs.app_id', '');
    const secretKey = _.get(chatbot, 'ui.talkjs.secret_key', '');
    const enabled = _.get(chatbot, 'ui.talkjs.enabled', false);
    return enabled && appId && secretKey;
  }, [chatbot]);

  const updateScheduleField = (field, value) => {
    setSchedule(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateDaySchedule = (day, field, value) => {
    if (field === 'enabled') {
      if (value) {
        // Enable day - add it to schedule with default times
        setSchedule(prev => ({
          ...prev,
          schedule: {
            ...prev.schedule,
            [day]: {
              enabled: true,
              start: prev.schedule[day]?.start || '09:00',
              end: prev.schedule[day]?.end || '17:00'
            }
          }
        }));
      } else {
        // Disable day - remove it from schedule completely
        setSchedule(prev => {
          const newSchedule = { ...prev.schedule };
          delete newSchedule[day];
          return {
            ...prev,
            schedule: newSchedule
          };
        });
      }
    } else {
      // Update other fields (start/end times)
      setSchedule(prev => ({
        ...prev,
        schedule: {
          ...prev.schedule,
          [day]: {
            ...prev.schedule[day],
            [field]: value
          }
        }
      }));
    }
  };

  const handleSave = async () => {
    if (!isTalkJSConfigured) {
      toast.warning("Please configure TalkJS first before setting up schedule");
      return;
    }

    setSaving(true);

    try {
      // Always send complete schedule via POST endpoint
      const schedulePayload = {
        project_id: projectId,
        timezone: schedule.timezone,
        enabled: schedule.enabled,
        schedule: schedule.schedule
      };
      
      await dispatch(Ai.actions.updateSupportSchedule(chatbot.id, schedulePayload));

      // Refresh support status after saving
      await fetchSupportStatus();

      toast.success('Support schedule saved successfully!');
    } catch (error) {
      console.error('Error saving support schedule:', error);
      toast.error('Failed to save support schedule');
    } finally {
      setSaving(false);
    }
  };

  const isLive = supportStatus === 'online';
  const isUnknown = supportStatus === 'unknown';

  return (
    <>
      <h3 className='RagChatbotDetailPage__section-title'>Support Schedule</h3>
      <div className='RagChatbotDetailPage__section-content'>
        {!isTalkJSConfigured && (
          <div className="RagChatbotDetailPage__feature-disabled-notice" style={{ marginBottom: '20px' }}>
            Please configure TalkJS in the Configuration tab first
          </div>
        )}

        {loading && (
          <div className="RagChatbotDetailPage__loading" style={{ marginBottom: '20px', textAlign: 'center' }}>
            Loading schedule data...
          </div>
        )}

        <div className='RagChatbotDetailPage__row'>
          <div className='RagChatbotDetailPage__input-wrap'>
            <div className='RagChatbotDetailPage__input-label'>
              Enable Scheduled Support
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={schedule.enabled}
                onChange={(e) => updateScheduleField('enabled', e.target.checked)}
                disabled={!isTalkJSConfigured || loading}
              />
              <span>Enable scheduled realtime support</span>
            </label>
          </div>
        </div>

        {schedule.enabled && (
          <>
            <div className='RagChatbotDetailPage__row'>
              <div className='RagChatbotDetailPage__input-wrap'>
                <div className='RagChatbotDetailPage__input-label'>Timezone</div>
                <select
                  className='RagChatbotDetailPage__input'
                  value={schedule.timezone}
                  onChange={(e) => updateScheduleField('timezone', e.target.value)}
                >
                  {generateTimezoneOptions().map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>
            </div>

        <div className='RagChatbotDetailPage__row'>
          <div className='RagChatbotDetailPage__input-wrap'>
            <div className='RagChatbotDetailPage__input-label'>Weekly Schedule</div>
            <div className='RagChatbotDetailPage__schedule-grid'>
              {DAYS_OF_WEEK.map(day => (
                <div key={day.key} className='RagChatbotDetailPage__schedule-day'>
                  <div className='RagChatbotDetailPage__schedule-day-header'>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '120px' }}>
                      <input
                        type="checkbox"
                        checked={!!schedule.schedule[day.key]}
                        onChange={(e) => updateDaySchedule(day.key, 'enabled', e.target.checked)}
                      />
                      <span>{day.label}</span>
                    </label>
                  </div>
                  {schedule.schedule[day.key] && (
                    <div className='RagChatbotDetailPage__schedule-times'>
                      <input
                        type="time"
                        value={schedule.schedule[day.key]?.start || '09:00'}
                        onChange={(e) => updateDaySchedule(day.key, 'start', e.target.value)}
                        className='RagChatbotDetailPage__input RagChatbotDetailPage__time-input'
                      />
                      <span className='RagChatbotDetailPage__time-separator'>to</span>
                      <input
                        type="time"
                        value={schedule.schedule[day.key]?.end || '17:00'}
                        onChange={(e) => updateDaySchedule(day.key, 'end', e.target.value)}
                        className='RagChatbotDetailPage__input RagChatbotDetailPage__time-input'
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className='RagChatbotDetailPage__row'>
          <div className='RagChatbotDetailPage__input-wrap'>
            <div className='RagChatbotDetailPage__input-label'>
              Current Status
            </div>
            <div className='RagChatbotDetailPage__status-indicator'>
              <span className={`RagChatbotDetailPage__status-badge ${
                isLive ? 'RagChatbotDetailPage__status-badge--live' : 
                isUnknown ? 'RagChatbotDetailPage__status-badge--unknown' : 
                'RagChatbotDetailPage__status-badge--offline'
              }`}>
                {isLive ? 'LIVE' : isUnknown ? 'DISABLED' : 'OFFLINE'}
              </span>
              <span className='RagChatbotDetailPage__status-text'>
                {isLive ? 'Support agents are currently available' : 
                 isUnknown ? 'Scheduled support is disabled' :
                 'Support is offline - customers will be contacted by email'}
              </span>
            </div>
          </div>
        </div>
          </>
        )}

        <div className='RagChatbotDetailPage__row center'>
          <Button
            color="green"
            title="Save Schedule"
            onClick={handleSave}
            loading={saving}
            disabled={saving || !isTalkJSConfigured || loading}
            size="large"
          />
        </div>
      </div>
    </>
  );
};